import { useState } from "react";

import ConfirmDialog from "@buildpy/ui/confirm-dialog";
import InfoTip from "@buildpy/ui/info-tip";
import { SimpleSelect } from "@buildpy/ui/select";
import { VAT_MODES, VatMode } from "@buildpy/core/modules/money/vat";
import { vatBasisLabel, vatModeShortLabel } from "@buildpy/core/modules/quote/snapshot-vat";
import { vatModeSwitchable } from "@buildpy/core/modules/quote/editor/reducer";
import { QuoteStatus } from "@buildpy/core/modules/quote/types";
import { lockedNow, openUnlessLocked, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";

// 앞에 「단가에 부가세」 머리말이 붙은 좁은 칸(w-24)이라 「부가세」 를 뺀 짧은 말을 쓴다.
export const VAT_MODE_OPTIONS: { value: VatMode; label: string }[] = VAT_MODES.map((value) => ({ value, label: vatModeShortLabel(value) }));

// 전환 확인 창의 한 줄 — 단가를 몇 개 어느 쪽으로 바꾸는지와 그 결과.
export const vatSwitchNoteOf = (next: VatMode, priceCount: number): string =>
  `단가 ${priceCount}개를 ${next === "EXCL" ? "÷1.1" : "×1.1"} · 원가 그대로 · 왕복 시 1원 차이 가능 · 직접 고친 절사액은 자동으로 돌아갑니다.`;

interface Props {
  value: VatMode;
  status: QuoteStatus;
  priceCount: number;
  onChange: (next: VatMode) => void;
  size?: "sm" | "lg";
  // 잠긴 견적 — 상태와 무관하게 고르기를 끈다.
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 잠긴 직후·재렌더 전에 눌린 「바꾸기」 도 버린다.
  isLocked: () => boolean;
}

// 「단가에 부가세 포함/별도」 고르기 — 바꾸기 전에 단가 변환을 확인받고, 작성 중이 아니면 잠근다.
const VatModeField = ({ value, status, priceCount, onChange, size = "sm", disabled = false, isLocked }: Props) => {
  const [pending, setPending] = useState<VatMode | null>(null);
  const switchable = vatModeSwitchable(status);
  return (
    <div className="flex flex-col gap-y-1">
      <span className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
        단가에 부가세
        {!switchable && (
          <InfoTip label="부가세 포함·별도 잠김 안내">
            작성 중인 견적에서만 바꿀 수 있습니다 — 이미 보낸 견적의 단가를 바꾸면 고객이 본 금액과 달라집니다.
          </InfoTip>
        )}
      </span>
      <div className="w-24">
        <SimpleSelect
          aria-label="단가에 부가세"
          disabled={!switchable || disabled}
          options={VAT_MODE_OPTIONS}
          size={size}
          value={value}
          onValueChange={(next) => {
            if (next !== value) setPending(next as VatMode);
          }}
        />
      </div>
      <ConfirmDialog
        confirmLabel="바꾸기"
        description={pending ? vatSwitchNoteOf(pending, priceCount) : undefined}
        open={openUnlessLocked(pending !== null, disabled)}
        testId="vat-mode-confirm"
        title={`단가를 ${vatBasisLabel(pending ?? value)}로 바꿀까요?`}
        onConfirm={unlessLocked(
          lockedNow(disabled, isLocked),
          () => {
            if (pending) onChange(pending);
          }
        )}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      />
    </div>
  );
};

export default VatModeField;
